import React, { useEffect, useState, useRef } from 'react';
import { GetCurrentUserInfo_User_Api, UpdateCurrentUserInfo_User_Api } from '../../API/userApi';
import { 
    Button, 
    Col, 
    Form, 
    Input, 
    Row, 
    Upload, 
    Card,
    Avatar,
    Space,
    Tooltip,
    Divider
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { 
    RollbackOutlined, 
    CheckOutlined, 
    UploadOutlined, 
    UserOutlined,
    EyeOutlined,
    EditOutlined,
    QuestionCircleOutlined,
    IdcardOutlined,
    HomeOutlined
} from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';

// -----------------------------------------------------------
// وارد کردن DatePicker و تنظیمات شمسی
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
// -----------------------------------------------------------

// =================================================================
const SERVER_DATE_FORMAT = "YYYY-MM-DD"; 

const persianToEnglishNumbers = (str) => {
    if (!str) return '';
    let output = String(str).trim(); 
    const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicNumbers = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    for (let i = 0; i < 10; i++) {
        output = output.replace(persianNumbers[i], englishNumbers[i]);
        output = output.replace(arabicNumbers[i], englishNumbers[i]);
    }
    return output.replace(/\//g, "-"); 
};

// =================================================================
const PersianDatePickerAdapter = ({ value, onChange, placeholder, onDateSelect }) => {
    const inputRef = useRef();

    const handleOnChange = (dateObject) => {
        let dateString = null;
        if (dateObject && !Array.isArray(dateObject)) {
            dateString = persianToEnglishNumbers(dateObject.format(SERVER_DATE_FORMAT));
        }
        
        onChange(dateString);
        if (onDateSelect && dateString) {
            onDateSelect(dateString);
        }
    };

    const getDateValue = () => {
        if (!value) return undefined;
        try {
            return new DateObject({
                calendar: persian,
                locale: persian_fa,
                date: persianToEnglishNumbers(value).replace(/\//g, "-"),
                format: SERVER_DATE_FORMAT
            });
        } catch (error) {
            return undefined;
        }
    };

    return (
        <div onKeyDown={(e) => {
            if (e.key === 'Tab') {
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            }
        }}>
            <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={getDateValue()}
                onChange={handleOnChange}
                inputClass="ant-input persian-date-input"
                calendarPosition="bottom-right"
                format={SERVER_DATE_FORMAT}
                placeholder={placeholder}
                editable={true}
                hideOnScroll={false}
                style={{ width: '100%' }}
            />
        </div>
    );
};

// =================================================================
// اعتبارسنجی‌ها
const validateTel = (_, value) => {
    if (!value) return Promise.reject(new Error('تلفن ثابت الزامی است!'));
    if (!/^0\d{2,3}\d{7,8}$/.test(value)) return Promise.reject(new Error('فرمت تلفن ثابت معتبر نیست'));
    return Promise.resolve();
};

const validatePostalCode = (_, value) => {
    if (!value) return Promise.reject(new Error('کد پستی الزامی است!'));
    if (!/^\d{10}$/.test(value)) return Promise.reject(new Error('کد پستی باید دقیقاً ۱۰ رقم باشد!'));
    return Promise.resolve();
};

const validateAddress = (_, value) => {
    if (!value) return Promise.reject(new Error('آدرس الزامی است!'));
    if (value.trim().length < 8) return Promise.reject(new Error('آدرس باید حداقل ۸ کاراکتر باشد!'));
    return Promise.resolve();
};

// کامپوننت Label با آیکون راهنما
const LabelWithHelp = ({ label, helpText }) => (
    <Space size="small">
        <span>{label}</span>
        <Tooltip title={helpText}>
            <QuestionCircleOutlined style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }} />
        </Tooltip>
    </Space>
);

export const UserProfileComponent = ({ setLoading, showMessage, cancelEdit, profileUpdated }) => {
    const [user, setUser] = useState();
    const [form] = Form.useForm();
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const [uploadFileList, setUploadFileList] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await GetCurrentUserInfo_User_Api();
            setLoading(false);
            if (data.messageStatus === "Successful") {
                const userData = data.messageData.data;
                setUser(userData);
                form.setFieldsValue({
                    ...userData,
                    shamsiBirthDate: userData.shamsiBirthDate || userData.birthDate
                });
                if (userData.photoUrl) {
                    setFilePreview(userData.photoUrl);
                }
                if (userData.birthPlaceId) {
                    setCityId(userData.birthPlaceId);
                }
            } else {
                showMessage(data.message, "error");
            }
        } catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error");
        }
    };

    const onSubmit = async (values) => {
        setLoading(true);
        try {
            values.birthPlaceId = cityId || user?.birthPlaceId;

            const formData = new FormData();
            if (file) {
                formData.append("photo", file);
            }

            if (user?.photoUrl && !file) {
                formData.append("previousPhotoUrl", user.photoUrl);
            }

            Object.entries(values).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, value.toString());
                }
            });

            let data = await UpdateCurrentUserInfo_User_Api(formData);
            setLoading(false);

            if (data.messageStatus === "Successful") {
                showMessage("اطلاعات پروفایل با موفقیت به‌روزرسانی شد", "success");
                if (profileUpdated) profileUpdated();
            } else {
                showMessage(data.message, "error");
            }
        } catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error");
        }
    };

    const onFinishFailed = (errorInfo) => {
        const errorFields = errorInfo.errorFields;
        let errorMessage = "خطا در تکمیل فرم:\n";
        if (errorFields && errorFields.length > 0) {
            errorFields.forEach(field => {
                const fieldName = field.name.join(' '); 
                const fieldErrors = field.errors.join(' | '); 
                errorMessage += `\n${fieldName}: ${fieldErrors}`;
            });
        }
        showMessage(errorMessage, "error");
    };

    const handleFileChange = (info) => {
        if (info.fileList.length > 0) {
            const selectedFile = info.fileList[0].originFileObj;
            if (selectedFile.size > 2 * 1024 * 1024) {
                showMessage("حجم فایل باید کمتر از ۲MB باشد", "error");
                return;
            }
            setFile(selectedFile);
            setUploadFileList(info.fileList);
            
            const reader = new FileReader();
            reader.onload = (e) => setFilePreview(e.target.result);
            reader.readAsDataURL(selectedFile);
        } else {
            setFile(null);
            setUploadFileList([]);
            setFilePreview(user?.photoUrl || null);
        }
    };

    return (
        <>
            {user ? (
                <div style={{ padding: '0', minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
                    <Card 
                        style={{ 
                            borderRadius: '0', 
                            boxShadow: 'none', 
                            minHeight: '100vh', 
                            margin: 0, 
                            padding: 0 
                        }}
                        bodyStyle={{ 
                            padding: '16px', 
                            margin: 0,
                            maxWidth: '100%'
                        }}
                    >
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '12px 0', 
                            marginBottom: '16px',
                            borderBottom: '1px solid #e8e8e8'
                        }}>
                            <Space>
                                <EditOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                                <span style={{ fontWeight: 'bold', fontSize: '18px' }}>پروفایل کاربری</span>
                            </Space>
                        </div>

                        <Form
                            form={form}
                            name="userProfileForm"
                            layout="horizontal"
                            onFinish={onSubmit}
                            onFinishFailed={onFinishFailed}
                            size="large"
                            labelCol={{ xs: 24, sm: 8, md: 6 }}
                            wrapperCol={{ xs: 24, sm: 16, md: 18 }}
                            requiredMark={false}
                        >
                            <Form.Item name="id" hidden={true} />

                            {/* اطلاعات شخصی */}
                            <Divider orientation="left" orientationMargin="0" style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '24px' }}>
                                <Space>
                                    <IdcardOutlined style={{ color: '#52c41a' }} />
                                    اطلاعات شخصی
                                </Space>
                            </Divider>

                            <Form.Item
                                label={<LabelWithHelp label="نام" helpText="نام خود را به فارسی وارد کنید" />}
                                name="name"
                                rules={[{ required: true, message: 'لطفاً نام را وارد کنید!' }]}
                            >
                                <Input placeholder="نام" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="نام خانوادگی" helpText="نام خانوادگی خود را به فارسی وارد کنید" />}
                                name="family"
                                rules={[{ required: true, message: 'لطفاً نام خانوادگی را وارد کنید!' }]}
                            >
                                <Input placeholder="نام خانوادگی" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="نام پدر" helpText="نام پدر خود را به فارسی وارد کنید" />}
                                name="fatherName"
                                rules={[{ required: true, message: 'لطفاً نام پدر را وارد کنید!' }]}
                            >
                                <Input placeholder="نام پدر" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="کد ملی" helpText="کد ملی شما" />}
                                name="nationalCode"
                            >
                                <Input 
                                    placeholder="کد ملی" 
                                    disabled
                                    style={{ textAlign: 'left', direction: 'ltr', background: '#f5f5f5' }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="تاریخ تولد" helpText="تاریخ تولد خود را به شمسی انتخاب کنید" />}
                                name="shamsiBirthDate"
                                rules={[{ required: true, message: 'تاریخ تولد الزامی است!' }]}
                            >
                                <PersianDatePickerAdapter placeholder="تاریخ تولد را انتخاب کنید" />
                            </Form.Item>

                            <Form.Item
                                label="شماره موبایل"
                                name="mobile"
                            >
                                <Input 
                                    disabled 
                                    style={{ textAlign: 'left', direction: 'ltr', background: '#f5f5f5' }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="تلفن ثابت" helpText="شماره تلفن ثابت با پیش‌شهر" />}
                                name="tel"
                                rules={[{ validator: validateTel }]}
                            >
                                <Input 
                                    placeholder="تلفن ثابت" 
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                    style={{ textAlign: 'left', direction: 'ltr' }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="کد پستی" helpText="کد پستی باید ۱۰ رقم باشد" />}
                                name="postalCode"
                                rules={[{ validator: validatePostalCode }]}
                            >
                                <Input 
                                    placeholder="کد پستی" 
                                    maxLength={10}
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                    style={{ textAlign: 'left', direction: 'ltr' }}
                                />
                            </Form.Item>
                            <Form.Item
    label={<LabelWithHelp label="شماره شبا" helpText=" شماره شبا جهت برگشت وجه واریزی در صورت نیاز" />}
    name="shebaNumber"
    rules={[
        {
            pattern: /^IR\d{24}$/,
            message: "شماره شبا باید با IR شروع شده و 24 رقم داشته باشد"
        }
    ]}
>
    <Input
        placeholder="IRxxxxxxxxxxxxxxxxxxxxxxxx"
        maxLength={26}
        style={{ direction: "ltr", textAlign: "left" }}
    />
</Form.Item>

                            {/* اطلاعات آدرس */}
                            <Divider orientation="left" orientationMargin="0" style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '24px' }}>
                                <Space>
                                    <HomeOutlined style={{ color: '#fa8c16' }} />
                                    اطلاعات آدرس
                                </Space>
                            </Divider>

                            <Form.Item
                                label={<LabelWithHelp label="محل تولد فعلی" helpText="محل تولد ثبت شده شما" />}
                            >
                                <Input 
                                    value={user.birthPlaceTitle} 
                                    readOnly 
                                    style={{ background: '#f5f5f5' }}
                                />
                            </Form.Item>

                            {/* تغییر محل تولد در صورت نیاز */}
                            <Form.Item
                                label={<LabelWithHelp label="تغییر محل تولد" helpText="در صورت نیاز می‌توانید استان و شهر محل تولد را تغییر دهید" />}
                            >
                                <Row gutter={16} style={{ marginTop: '8px' }}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label={<LabelWithHelp label="استان" helpText="استان محل تولد را انتخاب کنید" />}
                                            labelCol={{ span: 24 }}
                                            wrapperCol={{ span: 24 }}
                                            style={{ marginBottom: '16px' }}
                                        >
                                            <CustomTypeCombo
                                                setLoading={setLoading}
                                                showMessage={showMessage}
                                                placeHolder="انتخاب استان"
                                                parentId={2}
                                                onChange={setStateId}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label={<LabelWithHelp label="شهر" helpText="شهر محل تولد را انتخاب کنید" />}
                                            labelCol={{ span: 24 }}
                                            wrapperCol={{ span: 24 }}
                                            style={{ marginBottom: '16px' }}
                                        >
                                            <CustomTypeCombo
                                                setLoading={setLoading}
                                                showMessage={showMessage}
                                                placeHolder="انتخاب شهر"
                                                parentId={stateId}
                                                onChange={setCityId}
                                                disabled={!stateId}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="آدرس کامل" helpText="آدرس کامل با حداقل ۸ کاراکتر" />}
                                name="address"
                                rules={[{ validator: validateAddress }]}
                            >
                                <TextArea 
                                    placeholder="آدرس کامل"
                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                />
                            </Form.Item>

                            {/* تصویر پروفایل */}
                            <Divider orientation="left" orientationMargin="0" style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '24px' }}>
                                <Space>
                                    <UserOutlined />
                                    تصویر پروفایل
                                </Space>
                            </Divider>

                            <Form.Item
                                label={<LabelWithHelp label="عکس پروفایل" helpText="فرمت‌های مجاز: JPG, PNG, GIF - حداکثر سایز: ۲MB" />}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <Avatar
                                        size={60}
                                        src={filePreview}
                                        icon={!filePreview && <UserOutlined />}
                                        style={{ border: '1px solid #d9d9d9' }}
                                    />
                                    <Space direction="vertical" size="small">
                                        <Upload
                                            beforeUpload={() => false}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            maxCount={1}
                                            showUploadList={false}
                                            fileList={uploadFileList}
                                        >
                                            <Button icon={<UploadOutlined />} size="small">
                                                تغییر تصویر
                                            </Button>
                                        </Upload>
                                        {filePreview && (
                                            <Button 
                                                icon={<EyeOutlined />} 
                                                size="small"
                                                onClick={() => window.open(filePreview)}
                                                style={{ width: '100%' }}
                                            >
                                                مشاهده
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            </Form.Item>

                            {/* دکمه‌های اقدام */}
                            <Row className='sectionFooter'>
                                <Col xs={0} sm={0} md={8} lg={16} xl={16} />
                                <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                                    <Button
                                        style={{ width: '98%' }}
                                        className='btn-primary'
                                        htmlType="submit"
                                        onClick={() => form.submit()}>
                                        <CheckOutlined />
                                        ذخیره تغییرات
                                    </Button>
                                </Col>
                                <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                                    <Button
                                        className='btn-secondary'
                                        style={{ width: '100%' }}
                                        onClick={() => cancelEdit()}
                                    >
                                        <RollbackOutlined />
                                        لغو
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <p>در حال بارگذاری اطلاعات پروفایل...</p>
                </div>
            )}
        </>
    );
};





/*import React, { useEffect, useState ,useRef } from 'react';
import { GetCurrentUserInfo_User_Api, UpdateCurrentUserInfo_User_Api } from '../../API/userApi';
import { Button, Col, Form, Input, Row, Upload } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { RollbackOutlined, CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';

// -------------------------------------ش----------------------
// وارد کردن DatePicker و تنظیمات شمسی
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
// -----------------------------------------------------------

// =================================================================
const SERVER_DATE_FORMAT = "YYYY-MM-DD"; 

/**
 * تابع کمکی برای تبدیل ارقام فارسی/عربی به انگلیسی و تبدیل اسلش به خط تیره.
 */
/*const persianToEnglishNumbers = (str) => {
    if (!str) return '';
    let output = String(str).trim(); 

    const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicNumbers = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    for (let i = 0; i < 10; i++) {
        output = output.replace(persianNumbers[i], englishNumbers[i]);
        output = output.replace(arabicNumbers[i], englishNumbers[i]);
    }

    // جایگزینی اسلش با خط تیره
    return output.replace(/\//g, "-"); 
};

// =================================================================
/**
 * آداپتور سفارشی PersianDatePickerAdapter
 * این آداپتور با antd Form ادغام شده و خروجی YYYY-MM-DD انگلیسی را به Form.Item می‌دهد.
 */
/*import React, { useEffect, useState, useRef } from 'react'; // useRef رو اضافه کنید
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
*/
/*const PersianDatePickerAdapter = ({ value, onChange, placeholder, onDateSelect }) => {
    const inputRef = useRef();

    const handleOnChange = (dateObject) => {
        let dateString = null;
        if (dateObject && !Array.isArray(dateObject)) {
            dateString = persianToEnglishNumbers(dateObject.format(SERVER_DATE_FORMAT));
        }
        
        onChange(dateString);
        if (onDateSelect && dateString) {
            onDateSelect(dateString);
        }
    };

    const getDateValue = () => {
        if (!value) return undefined;
        try {
            return new DateObject({
                calendar: persian,
                locale: persian_fa,
                date: persianToEnglishNumbers(value).replace(/\//g, "-"),
                format: SERVER_DATE_FORMAT
            });
        } catch (error) {
            return undefined;
        }
    };

    return (
        <div onKeyDown={(e) => {
            if (e.key === 'Tab') {
                // فوکوس رو از هر المنت فعالی بردار
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            }
        }}>
            <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={getDateValue()}
                onChange={handleOnChange}
                inputClass="ant-input persian-date-input"
                calendarPosition="bottom-right"
                format={SERVER_DATE_FORMAT}
                placeholder={placeholder}
                editable={true}
                hideOnScroll={false}
            />
        </div>
    );
};
// =================================================================


export const UserProfileComponent = ({ setLoading, showMessage, cancelEdit, profileUpdated }) => {
    // =================================================================
    const [user, setUser] = useState();
    const [form] = Form.useForm()
    const [file, setFile] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    // =================================================================
    useEffect(() => {
        loadData();
    }, []);
    // =================================================================
    const loadData = async () => {
        setLoading(true);
        try {
            const data = await GetCurrentUserInfo_User_Api();
            setLoading(false);
            if (data.messageStatus === "Successful") {
                const userData = data.messageData.data;
                setUser(userData);
                form.setFieldsValue(userData);
            }
            else
                showMessage(data.message, "error")
        }
        catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error")
        }
    }
    // =================================================================
    const onSubmit = async (values) => {
        setLoading(true);
        try {
            // مقادیر from_Shamsi و to_Shamsi از values فرم خوانده می‌شوند.
            values.birthPlaceId = cityId;

        if (!cityId && user?.birthPlaceId) {
            values.birthPlaceId = user.birthPlaceId;
        }
            //////////////////////////////
            const formData = new FormData();
            if (file)
                formData.append("photo", file);

            // اضافه کردن مقادیر فرم به FormData (از جمله shamsiBirthDate که اکنون در values هست)
            Object.entries(values).forEach(([key, value]) => {
                 // **اصلاح اصلی:** اطمینان از اینکه تاریخ و سایر فیلدها به FormData اضافه می‌شوند.
                 // (توجه: value برای فیلد تاریخ از آداپتور به صورت string است.)
                if (value !== undefined && value !== null) {
                    formData.append(key, value);
                }
            });


// اگر cityId null است، از مقدار اصلی کاربر استفاده کنید
console.log(values);

            let data = await UpdateCurrentUserInfo_User_Api(formData);
            /////////////////////////////

            setLoading(false);

            if (data.messageStatus === "Successful") {
                showMessage(data.message, "Successful");
                if (profileUpdated) profileUpdated();
            }
            else
                showMessage(data.message, "error")
        }
        catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error")
        }
    };

    // =================================================================
    const onFinishFailed = (errorInfo) => {
        const errorFields = errorInfo.errorFields;
        let errorMessage = "خطا در تکمیل فرم. فیلدهای زیر مشکل دارند: \n";
        
        if (errorFields && errorFields.length > 0) {
            errorFields.forEach(field => {
                const fieldName = field.name.join(' '); 
                const fieldErrors = field.errors.join(' | '); 
                errorMessage += `\n ${fieldName}: ${fieldErrors}`;
            });
        } else {
             errorMessage = "خطا در تکمیل فرم: " + JSON.stringify(errorInfo);
        }

        showMessage(errorMessage, "error");
    };

    // =================================================================
    const handleFileChange = (info) => {
        // انتخاب اولین فایل
        if (info.fileList.length > 0) {
            setFile(info.fileList[0].originFileObj);
        } else {
            setFile(null);
        }
    };
    // =================================================================
    return (
        <>
            {user ?
                <>
                    <div className='sectionTitle'>
                        <h2 >
                            پروفایل
                        </h2>
                    </div>
                    <div className='container'>
                        <Form
                            form={form}
                            name="basic"
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 20 }}
                            onFinish={onSubmit}
                            onFinishFailed={onFinishFailed}
                        >

                            <Form.Item
                                name="id"
                                hidden={true}
                            />
                            <Form.Item
                                name="name"
                                label="نام"
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="family"
                                label="نام خانوادگی"
                            >
                                <Input />

                            </Form.Item>

                            <Form.Item
                                name="nationalCode"
                                label="کد ملی"
                            >
                                <Input style={{ textAlign: 'left' }} />
                            </Form.Item>

                            <Form.Item
                                name="fatherName"
                                label="نام پدر"
                            >
                                <Input />
                            </Form.Item>

                            {/* فیلد تاریخ تولد - از آداپتور تاریخ شمسی صحیح استفاده می‌شود *//*}
                            <Form.Item
                                name="shamsiBirthDate"
                                label="تاریخ تولد"
                            >
                                <PersianDatePickerAdapter />
                            </Form.Item>

                            <Form.Item
                                name="mobile"
                                label="شماره موبایل"
                            >
                                <Input disabled style={{ width: '100%', textAlign: 'left' }} />
                            </Form.Item>

                            <Form.Item
                                name="tel"
                                label="تلفن ثابت"
                            >
                                <Input style={{ textAlign: 'left' }} />
                            </Form.Item>

                            <Form.Item
                                name="postalCode"
                                label="کد پستی"
                            >
                                <Input style={{ textAlign: 'left' }} />
                            </Form.Item>

                            <Form.Item
                                name="birthPlaceTitle"
                                label="محل تولد"
                            >
                                <Input readOnly />
                            </Form.Item>

                            <Form.Item
                                label="تغییر محل تولد"
                            >
                                <Row justify={'space-between'}>
                                    <Col span={11}>
                                        <CustomTypeCombo
                                            setLoading={setLoading}
                                            showMessage={showMessage}
                                            placeHolder="استان تولد"
                                            parentId={2}
                                            onChange={(value) => { setStateId(value) }}
                                        />
                                    </Col>
                                    <Col span={11}>
                                        <CustomTypeCombo
                                            setLoading={setLoading}
                                            showMessage={showMessage}
                                            placeHolder="شهر تولد"
                                            parentId={stateId}
                                            onChange={(value) => { setCityId(value) }}
                                        />
                                    </Col>
                                </Row>
                            </Form.Item>

                            <Form.Item
                                name="address"
                                label="آدرس"
                            >
                                <TextArea placeholder="آدرس" className='full-width'
                                    autoSize={{ minRows: 2, maxRows: 6 }} />
                            </Form.Item>

                            <Form.Item
                                name="photoUrl"
                                label="عکس پروفایل"
                            >
                                <Row>
                                    <Col>
                                        {/* نمایش عکس فعلی *//*}
                                        {user.photoUrl && <img src={user.photoUrl} style={{ width: '100px', height: '100px', objectFit: 'cover' }} alt="Profile" />}
                                    </Col>
                                    <Col style={{ paddingTop: '40px', paddingRight: '10px' }}>
                                        <Upload
                                            style={{ width: '95%' }}
                                            beforeUpload={() => false}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            maxCount={1}
                                            listType="picture"
                                            fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []}
                                        >

                                            <Button icon={<UploadOutlined />} style={{ width: '100%' }}>تغییر تصویر پروفایل</Button>
                                        </Upload>

                                    </Col>
                                </Row>
                            </Form.Item>

                        </Form>
                    </div>

                    <Row className='sectionFooter'>
                        <Col xs={0} sm={0} md={8} lg={16} xl={16} />
                        <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                            <Button
                                style={{ width: '98%' }}
                                className='btn-primary'
                                htmlType="submit"
                                onClick={() => form.submit()}>
                                <CheckOutlined />
                                ذخیره
                            </Button>
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                            <Button
                                className='btn-secondary'
                                style={{ width: '100%' }}
                                onClick={() => cancelEdit()}
                            >
                                <RollbackOutlined />
                                لغو </Button>
                        </Col>
                    </Row>
                </> : <></>
            }
        </>
    )
}
    */